import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useSubscription } from '../hooks/useSubscription';
import { useSessionMonitor } from '../hooks/useSessionMonitor';
import authService from '../services/authService';
import { hasRoleAccess } from '../constants/roles';

/**
 * Decode JWT expiry (ms) without verification — client-side only.
 */
function getTokenExpiryMs(token) {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    const payload = JSON.parse(json);
    return payload.exp ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

/**
 * `requiredRole` gates the route on the shared hierarchy. Omit it and the route
 * only requires authentication, as before.
 *
 * A route-level guard, not just an in-component one: AdminDashboard already
 * rendered its own "Access Denied" body, but the route still MOUNTED for anyone
 * logged in, so the page and its data-fetching children were reachable by URL.
 * This refuses before the element renders.
 */
const ProtectedRoute = ({ children, requiredRole }) => {
  const { isUserAuthenticated, userLoading, userToken, user } = useAuth();
  const { fetchSubscription, fetchEntitlements, initialized } = useSubscription();
  const location = useLocation();

  // Track whether we've validated the session on this mount (page refresh)
  const [sessionChecked, setSessionChecked] = useState(false);
  const [sessionValid, setSessionValid] = useState(true);

  // Proactively monitor token expiration and refresh before it expires
  useSessionMonitor();

  // On mount: if we appear authenticated but the token is expired,
  // try a silent refresh before rendering the app. This prevents the
  // flash of broken UI when returning after the JWT has expired.
  useEffect(() => {
    if (!isUserAuthenticated || !userToken) {
      setSessionChecked(true);
      return;
    }

    const expiryMs = getTokenExpiryMs(userToken);

    // Token is still valid — no need to refresh
    if (expiryMs && expiryMs > Date.now()) {
      setSessionChecked(true);
      return;
    }

    // Token is expired — attempt silent refresh via httpOnly cookie
    let cancelled = false;
    (async () => {
      try {
        const result = await authService.refreshJwtToken();
        if (cancelled) return;

        if (result.success) {
          setSessionValid(true);
        } else {
          // Refresh failed — session is truly expired
          authService.logoutUser();
          setSessionValid(false);
        }
      } catch {
        if (!cancelled) {
          authService.logoutUser();
          setSessionValid(false);
        }
      } finally {
        if (!cancelled) {
          setSessionChecked(true);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only on mount

  // Fetch subscription data and entitlements when user is authenticated
  useEffect(() => {
    if (isUserAuthenticated && !initialized && sessionChecked && sessionValid) {
      fetchSubscription();
      fetchEntitlements();
    }
  }, [isUserAuthenticated, initialized, sessionChecked, sessionValid, fetchSubscription, fetchEntitlements]);

  // Still checking session or loading user
  if (userLoading || !sessionChecked) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-sage-50 via-mint-25 to-lavender-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-lavender-500 mx-auto mb-4"></div>
          <p className="text-sage-600 text-lg">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isUserAuthenticated || !sessionValid) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Authenticated but under-privileged: send them somewhere they CAN be rather
  // than a dead end, and `replace` so Back does not bounce them off it again.
  // Not a 404 — pretending the route does not exist would also hide it from the
  // super_admin who mistyped their way here.
  if (requiredRole && !hasRoleAccess(user?.role, requiredRole)) {
    return <Navigate to="/" replace />;
  }

  return children;
};

export default ProtectedRoute;
